// @flow

import {useCallback, useEffect, useRef, useState} from 'react';
import environment from '../../environment';

export function useIsMobile() {
  const [width, setWidth] = useState<number>(window.innerWidth);
  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);
  return width <= 768;
}

export function loadJS(
  url: string,
  id: string = '',
  async: boolean = false,
  defer: boolean = false,
  onLoad?: () => void
) {
  const script = document.createElement('script');
  script.setAttribute('src', url);
  if (id) script.setAttribute('id', id);
  if (async) script.async = true;
  if (defer) script.defer = true;
  if (typeof onLoad === 'function')
    script.addEventListener('load', onLoad, false);
  if (document.body) document.body.appendChild(script);
  return script;
}

export function useGoogleMapsApi(name: string) {
  const [googleApi, setGoogleApi] = useState();

  useEffect(() => {
    if (window.google) {
      // if window.google object is already available just use it
      setGoogleApi(window.google);
      return;
    }
    const src =
      'https://maps.googleapis.com/maps/api/js?key=' +
      environment.googleKey +
      '&libraries=places&v=weekly';

    loadJS(src, '', true, true, () => setGoogleApi(window.google));
  }, [googleApi]);

  return googleApi;
}

export function scrollTop(
  behavior: 'auto' | 'smooth',
  position: ?number,
  top: ?number
) {
  const topPos = top || 0;
  if (window) {
    if (window.scrollTo) {
      try {
        if (position) window.scrollTo({top: topPos, behavior}, position);
        else window.scrollTo({top: topPos, behavior});
      } catch {
        window.scrollTo(topPos, 0);
      }
    } else if (document) {
      if (document.body) document.body.scrollTop = topPos; // For Safari
      if (document.documentElement) document.documentElement.scrollTop = topPos; // For Chrome, Firefox, IE and Opera
    }
  }
}

export function useClickOutside(ref: any, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    window.document.addEventListener('click', handleClickOutside);

    return () => {
      window.document.removeEventListener('click', handleClickOutside);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
}

export async function getClipboardFile(event: any): Promise<File[]> {
  const clipboardItems =
    navigator &&
    navigator.clipboard &&
    typeof navigator.clipboard.read === 'function'
      ? await navigator.clipboard.read()
      : event.clipboardData.files;

  const files: File[] = [];

  //$FlowFixMe
  for (const clipboardItem of clipboardItems) {
    if (
      clipboardItem &&
      clipboardItem.type &&
      clipboardItem.type.startsWith('image/')
    ) {
      const file = new File(
        [clipboardItem],
        `${crypto.randomUUID()}.${clipboardItem.type}`,
        {
          type: clipboardItem.type,
        }
      );

      files.push(file);
    } else {
      const imageTypes = clipboardItem.types.filter(type =>
        type.startsWith('image/')
      );

      for (const imageType of imageTypes) {
        const blob = await clipboardItem.getType(imageType);

        const file = new File([blob], `${crypto.randomUUID()}.${imageType}`, {
          type: imageType,
        });

        files.push(file);
      }
    }
  }

  return files;
}

export function useOutsideAlerter() {
  const ref = useRef<HTMLElement | null>(null);
  const [isOutside, setIsOutside] = useState<boolean>(false);

  const handleClickOutside = useCallback((event: any) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsOutside(true);
    } else setIsOutside(false);
  }, []);

  useEffect(() => {
    if (document) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      if (document)
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, ref]);

  return {ref, isOutside};
}

export function updateLocalStorage<T>(key: string, data: T) {
  try {
    const dataString = JSON.stringify(data);

    window.localStorage.setItem(key, dataString);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function getLocalStorage<T>(key: string): T | null {
  try {
    const dataString = window.localStorage.getItem(key);

    if (dataString) {
      return JSON.parse(dataString);
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
}

export function useSessionStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
}
